import Coordinate from "@/domains/coordinates/coordinate";
import { useSavedLocationStore } from "@/domains/locations/savedLocationStore";
import { useTripStore } from "@/domains/trips";
import { useVehicleStore } from "@/domains/vehicles";
import {
  getVehicleLocationFreshness,
  type VehicleLocationFreshness,
} from "@/domains/vehicles/vehicleLocationFreshness";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useDashboardNavStore } from "./dashboardNavStore";

const VEHICLE_ICON_FILENAME_BY_FRESHNESS: Record<
  VehicleLocationFreshness,
  string
> = {
  fresh: "luxury-car-shadow.svg",
  stale: "car-greyed-out-1.svg",
};

/** Vehicle marker icon URL for a given location freshness tier. */
function vehicleIconSrc(freshness: VehicleLocationFreshness): string {
  return `${window.location.origin}/images/car_images/${VEHICLE_ICON_FILENAME_BY_FRESHNESS[freshness]}`;
}

/**
 * Map render coordinate.
 *
 * [longitude, latitude, intensity]
 *
 * intensity is a normalised 0-1 value consumed by
 * gradient rendering. It is not geographic altitude.
 */
export type MapGradientPoint = [
  latitude: number,
  longitude: number,
  intensity: number,
];

export type MarkerData = {
  latitude: number;
  longitude: number;
  iconSrc?: string;
  onClick?: () => void;
};

type MarkerDataWithRotation = MarkerData & {
  rotation?: number;
};

export const useMapStore = defineStore("mapStore", () => {
  const tripStore = useTripStore();
  const vehicleStore = useVehicleStore();
  const savedLocationStore = useSavedLocationStore();
  const dashboardNavStore = useDashboardNavStore();

  /** the default map center, balancing for over lapping UI elements. */
  const visualCenterOfNZ = new Coordinate({
    latitude: -41.2924,
    longitude: 174.7787,
  });

  /** approx IP location of the user. Used for address look up bias and user related map centring */
  const geoIp = ref<Coordinate | null>(null);

  /**
   * coordinates for the map to preform a fly to animation to.
   * Note: the map will clear this once the animation is complete.
   */
  const flyTo = ref<Coordinate | null>(null);

  /** coordinates for the center of the map that the user has panned to. Exposed for address look up bias. */
  const pannedCenter = ref<Coordinate | null>(null);

  const getMarkers = computed<MarkerData[]>(() => {
    const collectorArray: MarkerData[] = [];
    const trip = tripStore.getSelectedTrip;

    if (trip) {
      // A trip is displayed: only its own itinerary is shown, same split as
      // getMarkersWithRotation does for the trip's vehicle below - saved
      // locations aren't shown alongside it.
      trip.locations.forEach((l, i) => {
        if (i === 0) {
          collectorArray.push({
            latitude: l.coordinates.latitude,
            longitude: l.coordinates.longitude,
            iconSrc: `${window.location.origin}/images/icons/Start Location.svg`,
          });
        } else {
          collectorArray.push({
            latitude: l.coordinates.latitude,
            longitude: l.coordinates.longitude,
            iconSrc: `${window.location.origin}/images/icons/Pin.svg`,
          });
        }
      });

      return collectorArray;
    }

    return collectorArray;
  });

  const getClusteredMarkers = computed<MarkerData[]>(() => {
    const collectorArray: MarkerData[] = [];
    const trip = tripStore.getSelectedTrip;
    if (trip) return collectorArray;

    // No trip displayed: show every saved location, opening its dashboard
    // details page on click.
    for (const location of savedLocationStore.getSavedLocations) {
      collectorArray.push({
        latitude: location.coordinates.latitude,
        longitude: location.coordinates.longitude,
        iconSrc: `${window.location.origin}/images/icons/Pin Filled.svg`,
        onClick: () => dashboardNavStore.showLocationDetails(location.localId),
      });
    }

    return collectorArray;
  });

  const getMarkersWithRotation = computed<MarkerDataWithRotation[]>(() => {
    const collectorArray: MarkerDataWithRotation[] = [];

    // A bearing is already degrees clockwise from north, same as CSS
    // `rotate()` on an icon whose artwork points north at 0deg, so it maps
    // straight across with no conversion.
    const trip = tripStore.getSelectedTrip;

    if (trip) {
      // A trip is displayed: only its own vehicle is shown, and only if it
      // has a recorded location. No other fleet vehicles are shown.
      const vehicle = trip.vehicle;
      if (!vehicle?.externalId) return collectorArray;

      const location = vehicleStore.getLocationForVehicle(vehicle.externalId);
      if (!location || location.latitude == null || location.longitude == null)
        return collectorArray;

      collectorArray.push({
        latitude: location.latitude,
        longitude: location.longitude,
        iconSrc: vehicleIconSrc(getVehicleLocationFreshness(location)),
        rotation: location.bearing ?? 0,
        onClick: () => dashboardNavStore.showVehicleDetails(vehicle.id),
      });

      return collectorArray;
    }

    // No trip displayed: show every fleet vehicle that has a recorded
    // location.
    for (const vehicle of vehicleStore.vehicles) {
      if (!vehicle.externalId) continue;

      const location = vehicleStore.getLocationForVehicle(vehicle.externalId);
      if (!location || location.latitude == null || location.longitude == null) continue;

      collectorArray.push({
        latitude: location.latitude,
        longitude: location.longitude,
        iconSrc: vehicleIconSrc(getVehicleLocationFreshness(location)),
        rotation: location.bearing ?? 0,
        onClick: () => dashboardNavStore.showVehicleDetails(vehicle.id),
      });
    }

    return collectorArray;
  });

  const getGradientPolylines = computed<MapGradientPoint[][]>(() => {
    const trip = tripStore.getSelectedTrip;
    return trip?.itinerary?.getGradientPointsByLeg() ?? [];
  });

  const getCenter = computed<Coordinate>(() => {
    return pannedCenter.value ?? geoIp.value ?? visualCenterOfNZ;
  });

  return {
    flyTo,
    geoIp,
    pannedCenter,
    getCenter,
    getClusteredMarkers,
    getMarkers,
    getMarkersWithRotation,
    getGradientPolylines,
  };
});
